import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ruler, 
  MousePointer2, 
  Check, 
  X, 
  RotateCcw, 
  Info,
  ChevronDown,
  ChevronUp,
  Crosshair,
  Move,
  MapPin,
  Save,
  Trash2,
  Eye,
  EyeOff,
  SlidersHorizontal,
  Car,
  Columns,
  ArrowLeftRight,
  SkipBack,
  SkipForward,
  Play,
  Pause,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

interface Point {
  x: number;
  y: number;
}

interface SavedCalibration {
  id: string;
  name: string;
  pixelsPerMeter: number;
  createdAt: string;
}

interface ReferenceOverlay {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  defaultMeters: number;
}

interface CalibrationPanelProps {
  file: File | null;
  pixelsPerMeter: number;
  onCalibrationComplete: (pixelsPerMeter: number) => void;
}

const STORAGE_KEY = 'velocityai-calibrations';

// Reference overlays for common objects
const REFERENCE_OVERLAYS: ReferenceOverlay[] = [
  { id: 'lane', name: 'Carril estándar', icon: <Columns className="w-4 h-4" />, description: 'Ancho típico de carril', defaultMeters: 3.5 },
  { id: 'car', name: 'Coche estándar', icon: <Car className="w-4 h-4" />, description: 'Largo de un coche medio', defaultMeters: 4.5 },
  { id: 'crosswalk', name: 'Paso de peatones', icon: <ArrowLeftRight className="w-4 h-4" />, description: 'Ancho del paso', defaultMeters: 4.0 },
  { id: 'line', name: 'Línea discontinua', icon: <Ruler className="w-4 h-4" />, description: 'Longitud de marca vial', defaultMeters: 2.0 },
];

export function CalibrationPanel({ 
  file, 
  pixelsPerMeter, 
  onCalibrationComplete 
}: CalibrationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [points, setPoints] = useState<Point[]>([]);
  const [realDistance, setRealDistance] = useState<string>('');
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [calculatedPPM, setCalculatedPPM] = useState<number | null>(null);
  
  // New state for enhanced features
  const [showOverlay, setShowOverlay] = useState(true);
  const [selectedReference, setSelectedReference] = useState<string | null>(null);
  const [savedCalibrations, setSavedCalibrations] = useState<SavedCalibration[]>([]);
  const [locationName, setLocationName] = useState('');
  const [sliderValue, setSliderValue] = useState(pixelsPerMeter);
  const [showSlider, setShowSlider] = useState(false);
  const [previewSpeed, setPreviewSpeed] = useState(50); // km/h for preview
  
  // Frame selection state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(30); // Assumed FPS
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load saved calibrations from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setSavedCalibrations(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading calibrations:', e);
    }
  }, []);

  // Save calibrations to localStorage
  const saveCalibrations = (calibrations: SavedCalibration[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(calibrations));
      setSavedCalibrations(calibrations);
    } catch (e) {
      console.error('Error saving calibrations:', e);
    }
  };

  // Create video URL when file changes
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  // Handle video metadata loaded
  const handleVideoLoaded = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setDuration(video.duration);
      // Try to estimate FPS (default to 30 if not available)
      setFps(30);
    }
  }, []);

  // Handle video time update
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
    }
  }, []);

  // Frame control functions
  const seekToTime = useCallback((time: number) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(video.currentTime);
    }
  }, [duration]);

  const stepFrame = useCallback((direction: 'forward' | 'backward', frames: number = 1) => {
    const video = videoRef.current;
    if (video) {
      const frameTime = 1 / fps;
      const newTime = direction === 'forward' 
        ? video.currentTime + (frameTime * frames)
        : video.currentTime - (frameTime * frames);
      seekToTime(newTime);
    }
  }, [fps, seekToTime]);

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  // Format time as MM:SS.ms
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  // Get current frame number
  const getCurrentFrame = (): number => {
    return Math.floor(currentTime * fps);
  };

  // Update slider when pixelsPerMeter changes
  useEffect(() => {
    setSliderValue(pixelsPerMeter);
  }, [pixelsPerMeter]);

  // Calculate pixel distance between two points
  const getPixelDistance = useCallback((p1: Point, p2: Point): number => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }, []);

  // Handle canvas click to add calibration points
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isCalibrating || points.length >= 2) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = video.videoWidth / rect.width;
    const scaleY = video.videoHeight / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const newPoints = [...points, { x, y }];
    setPoints(newPoints);

    // If we now have 2 points and a reference is selected, auto-fill distance
    if (newPoints.length === 2 && selectedReference) {
      const ref = REFERENCE_OVERLAYS.find(r => r.id === selectedReference);
      if (ref) {
        setRealDistance(ref.defaultMeters.toString());
      }
    }
  }, [isCalibrating, points, selectedReference]);

  // Draw calibration overlay on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || !isCalibrating) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match canvas size to video
    canvas.width = video.videoWidth || video.clientWidth;
    canvas.height = video.videoHeight || video.clientHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw semi-transparent overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw reference overlay guides if enabled
    if (showOverlay && points.length === 0) {
      // Grid lines for reference
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 1;
      
      // Vertical lines
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo((canvas.width / 4) * i, 0);
        ctx.lineTo((canvas.width / 4) * i, canvas.height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let i = 1; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(0, (canvas.height / 3) * i);
        ctx.lineTo(canvas.width, (canvas.height / 3) * i);
        ctx.stroke();
      }

      // Draw reference hints based on selected type
      if (selectedReference) {
        const ref = REFERENCE_OVERLAYS.find(r => r.id === selectedReference);
        if (ref) {
          ctx.fillStyle = 'rgba(99, 102, 241, 0.8)';
          ctx.font = 'bold 16px JetBrains Mono';
          ctx.textAlign = 'center';
          ctx.fillText(`📏 ${ref.name}: ${ref.defaultMeters}m`, canvas.width / 2, 40);
          ctx.font = '12px JetBrains Mono';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
          ctx.fillText(ref.description, canvas.width / 2, 60);
        }
      }
    }

    // Draw crosshair guide in center
    if (points.length === 0) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([10, 10]);
      
      // Vertical line
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2, 0);
      ctx.lineTo(canvas.width / 2, canvas.height);
      ctx.stroke();
      
      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 2);
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
      
      ctx.setLineDash([]);
    }

    // Draw points
    points.forEach((point, index) => {
      // Outer glow
      ctx.beginPath();
      ctx.arc(point.x, point.y, 20, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(99, 102, 241, 0.3)';
      ctx.fill();

      // Inner circle
      ctx.beginPath();
      ctx.arc(point.x, point.y, 12, 0, Math.PI * 2);
      ctx.fillStyle = '#6366f1';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Crosshair
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(point.x - 20, point.y);
      ctx.lineTo(point.x - 8, point.y);
      ctx.moveTo(point.x + 8, point.y);
      ctx.lineTo(point.x + 20, point.y);
      ctx.moveTo(point.x, point.y - 20);
      ctx.lineTo(point.x, point.y - 8);
      ctx.moveTo(point.x, point.y + 8);
      ctx.lineTo(point.x, point.y + 20);
      ctx.stroke();

      // Point label
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px JetBrains Mono';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${index + 1}`, point.x, point.y);
    });

    // Draw line between points
    if (points.length === 2) {
      const [p1, p2] = points;
      
      // Dashed line
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Distance label
      const midX = (p1.x + p2.x) / 2;
      const midY = (p1.y + p2.y) / 2;
      const pixelDist = getPixelDistance(p1, p2);

      ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
      const labelWidth = 140;
      const labelHeight = 30;
      ctx.beginPath();
      ctx.roundRect(midX - labelWidth / 2, midY - labelHeight / 2 - 20, labelWidth, labelHeight, 6);
      ctx.fill();

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px JetBrains Mono';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${pixelDist.toFixed(0)} px`, midX, midY - 20);
    }
  }, [points, isCalibrating, showOverlay, selectedReference, getPixelDistance]);

  // Calculate pixels per meter when distance is entered
  useEffect(() => {
    if (points.length === 2 && realDistance) {
      const distanceValue = parseFloat(realDistance);
      if (distanceValue > 0) {
        const pixelDist = getPixelDistance(points[0], points[1]);
        const ppm = pixelDist / distanceValue;
        setCalculatedPPM(ppm);
      }
    } else {
      setCalculatedPPM(null);
    }
  }, [points, realDistance, getPixelDistance]);

  // Start calibration mode
  const startCalibration = () => {
    setIsCalibrating(true);
    setPoints([]);
    setRealDistance('');
    setCalculatedPPM(null);
    setSelectedReference(null);
    
    // Pause video at current frame (don't reset to 0, let user choose frame)
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  // Reset calibration
  const resetCalibration = () => {
    setPoints([]);
    setRealDistance('');
    setCalculatedPPM(null);
  };

  // Cancel calibration
  const cancelCalibration = () => {
    setIsCalibrating(false);
    setPoints([]);
    setRealDistance('');
    setCalculatedPPM(null);
    setSelectedReference(null);
  };

  // Apply calibration
  const applyCalibration = () => {
    if (calculatedPPM && calculatedPPM > 0) {
      onCalibrationComplete(calculatedPPM);
      setIsCalibrating(false);
    }
  };

  // Save current calibration as location
  const saveCurrentCalibration = () => {
    if (!locationName.trim()) return;
    
    const newCalibration: SavedCalibration = {
      id: Date.now().toString(),
      name: locationName.trim(),
      pixelsPerMeter: calculatedPPM || sliderValue,
      createdAt: new Date().toISOString(),
    };
    
    saveCalibrations([...savedCalibrations, newCalibration]);
    setLocationName('');
  };

  // Load saved calibration
  const loadCalibration = (calibration: SavedCalibration) => {
    onCalibrationComplete(calibration.pixelsPerMeter);
    setSliderValue(calibration.pixelsPerMeter);
  };

  // Delete saved calibration
  const deleteCalibration = (id: string) => {
    saveCalibrations(savedCalibrations.filter(c => c.id !== id));
  };

  // Apply slider value
  const applySliderValue = () => {
    onCalibrationComplete(sliderValue);
    setShowSlider(false);
  };

  // Calculate preview movement (how many pixels a car moving at previewSpeed would move per frame)
  const getPreviewInfo = (ppm: number) => {
    const fps = 30;
    const metersPerSecond = previewSpeed / 3.6;
    const pixelsPerSecond = metersPerSecond * ppm;
    const pixelsPerFrame = pixelsPerSecond / fps;
    return {
      pixelsPerFrame: pixelsPerFrame.toFixed(1),
      metersPerSecond: metersPerSecond.toFixed(1),
    };
  };

  if (!file) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl overflow-hidden"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-dark-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Ruler className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-dark-100">Calibración de Escala</h3>
            <p className="text-sm text-dark-400">
              {pixelsPerMeter.toFixed(1)} píxeles/metro
              {calculatedPPM && isCalibrating && (
                <span className="text-green-400 ml-2">
                  → {calculatedPPM.toFixed(1)} px/m (nuevo)
                </span>
              )}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-dark-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-dark-400" />
        )}
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-dark-800/50 space-y-4">
              
              {/* Quick Slider Adjustment */}
              <div className="bg-dark-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-primary-400" />
                    <span className="text-sm font-medium text-dark-200">Ajuste Rápido</span>
                  </div>
                  <button
                    onClick={() => setShowSlider(!showSlider)}
                    className="text-xs text-primary-400 hover:text-primary-300"
                  >
                    {showSlider ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                
                <AnimatePresence>
                  {showSlider && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-4"
                    >
                      {/* Slider */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-dark-400">1 px/m</span>
                          <span className="text-lg font-mono font-bold text-primary-400">
                            {sliderValue.toFixed(1)} px/m
                          </span>
                          <span className="text-xs text-dark-400">50 px/m</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="50"
                          step="0.5"
                          value={sliderValue}
                          onChange={(e) => setSliderValue(parseFloat(e.target.value))}
                          className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer
                                   [&::-webkit-slider-thumb]:appearance-none
                                   [&::-webkit-slider-thumb]:w-5
                                   [&::-webkit-slider-thumb]:h-5
                                   [&::-webkit-slider-thumb]:bg-primary-500
                                   [&::-webkit-slider-thumb]:rounded-full
                                   [&::-webkit-slider-thumb]:cursor-pointer
                                   [&::-webkit-slider-thumb]:shadow-lg"
                        />
                      </div>

                      {/* Preview Speed Selector */}
                      <div className="bg-dark-900/50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-dark-400">Velocidad de prueba:</span>
                          <div className="flex gap-2">
                            {[30, 50, 80, 120].map(speed => (
                              <button
                                key={speed}
                                onClick={() => setPreviewSpeed(speed)}
                                className={`px-2 py-1 text-xs rounded ${
                                  previewSpeed === speed
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
                                }`}
                              >
                                {speed} km/h
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Real-time preview */}
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div className="bg-dark-800 rounded p-2 text-center">
                            <p className="text-xs text-dark-500">Movimiento/frame</p>
                            <p className="text-lg font-mono font-bold text-amber-400">
                              {getPreviewInfo(sliderValue).pixelsPerFrame} px
                            </p>
                          </div>
                          <div className="bg-dark-800 rounded p-2 text-center">
                            <p className="text-xs text-dark-500">Resolución</p>
                            <p className="text-lg font-mono font-bold text-green-400">
                              {(100 / sliderValue).toFixed(1)} cm/px
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Apply slider button */}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={applySliderValue}
                        className="w-full py-2 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        Aplicar {sliderValue.toFixed(1)} px/m
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Saved Locations */}
              {savedCalibrations.length > 0 && (
                <div className="bg-dark-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-green-400" />
                    <span className="text-sm font-medium text-dark-200">Ubicaciones Guardadas</span>
                  </div>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {savedCalibrations.map(cal => (
                      <div
                        key={cal.id}
                        className="flex items-center justify-between bg-dark-900/50 rounded-lg px-3 py-2"
                      >
                        <div>
                          <p className="text-sm text-dark-200">{cal.name}</p>
                          <p className="text-xs text-dark-500">{cal.pixelsPerMeter.toFixed(1)} px/m</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadCalibration(cal)}
                            className="p-1.5 text-primary-400 hover:bg-dark-700 rounded transition-colors"
                            title="Cargar"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteCalibration(cal.id)}
                            className="p-1.5 text-red-400 hover:bg-dark-700 rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-dark-800/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-dark-300 space-y-2">
                    <p className="font-medium text-dark-100">¿Cómo calibrar con precisión?</p>
                    <ol className="list-decimal list-inside space-y-1 text-dark-400">
                      <li>Selecciona un <strong className="text-dark-200">objeto de referencia</strong> abajo</li>
                      <li>Haz clic en <strong className="text-dark-200">"Iniciar Calibración"</strong></li>
                      <li>Marca los <strong className="text-dark-200">2 extremos</strong> del objeto en el video</li>
                      <li>Verifica la distancia y aplica</li>
                    </ol>
                  </div>
                </div>
              </div>

              {!isCalibrating ? (
                <>
                  {/* Reference Objects */}
                  <div>
                    <p className="text-xs text-dark-400 mb-2 flex items-center gap-2">
                      <Eye className="w-3 h-3" />
                      Selecciona un objeto de referencia:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {REFERENCE_OVERLAYS.map(ref => (
                        <button
                          key={ref.id}
                          onClick={() => setSelectedReference(selectedReference === ref.id ? null : ref.id)}
                          className={`p-3 rounded-lg text-left transition-all ${
                            selectedReference === ref.id
                              ? 'bg-primary-500/20 border-2 border-primary-500'
                              : 'bg-dark-800/50 border-2 border-transparent hover:bg-dark-700/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={selectedReference === ref.id ? 'text-primary-400' : 'text-dark-400'}>
                              {ref.icon}
                            </span>
                            <span className={`text-sm font-medium ${
                              selectedReference === ref.id ? 'text-primary-300' : 'text-dark-200'
                            }`}>
                              {ref.name}
                            </span>
                          </div>
                          <p className="text-xs text-dark-500">{ref.defaultMeters}m típico</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start calibration button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={startCalibration}
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <Crosshair className="w-5 h-5" />
                    Iniciar Calibración
                    {selectedReference && (
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                        {REFERENCE_OVERLAYS.find(r => r.id === selectedReference)?.name}
                      </span>
                    )}
                  </motion.button>
                </>
              ) : (
                /* Calibration mode */
                <div className="space-y-4">
                  {/* Overlay toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dark-400">Mostrar guías de referencia</span>
                    <button
                      onClick={() => setShowOverlay(!showOverlay)}
                      className={`p-2 rounded-lg transition-colors ${
                        showOverlay ? 'bg-primary-500 text-white' : 'bg-dark-800 text-dark-400'
                      }`}
                    >
                      {showOverlay ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Video with calibration overlay */}
                  <div 
                    ref={containerRef}
                    className="relative rounded-xl overflow-hidden bg-black"
                  >
                    <video
                      ref={videoRef}
                      src={videoSrc || undefined}
                      className="w-full aspect-video object-contain"
                      muted
                      onLoadedMetadata={handleVideoLoaded}
                      onTimeUpdate={handleTimeUpdate}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                    />
                    <canvas
                      ref={canvasRef}
                      onClick={handleCanvasClick}
                      className={`absolute inset-0 w-full h-full ${
                        points.length < 2 ? 'cursor-crosshair' : 'cursor-default'
                      }`}
                    />

                    {/* Calibration status overlay */}
                    <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                      <div className="px-3 py-2 bg-dark-900/90 rounded-lg text-sm">
                        <span className="text-dark-400">Puntos: </span>
                        <span className={`font-mono font-bold ${
                          points.length === 2 ? 'text-green-400' : 'text-amber-400'
                        }`}>
                          {points.length}/2
                        </span>
                      </div>
                      
                      {/* Frame indicator */}
                      <div className="px-3 py-2 bg-dark-900/90 rounded-lg text-sm">
                        <span className="text-dark-400">Frame: </span>
                        <span className="font-mono font-bold text-primary-400">
                          {getCurrentFrame()}
                        </span>
                        <span className="text-dark-500 ml-2">{formatTime(currentTime)}</span>
                      </div>
                      
                      {points.length < 2 && (
                        <div className="px-3 py-2 bg-amber-500/90 rounded-lg text-sm text-white flex items-center gap-2">
                          <MousePointer2 className="w-4 h-4" />
                          Haz clic para marcar el punto {points.length + 1}
                        </div>
                      )}
                    </div>

                    {/* Move instruction */}
                    {points.length === 0 && (
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-dark-900/90 rounded-lg text-sm text-dark-300 flex items-center gap-2">
                        <Move className="w-4 h-4" />
                        Selecciona dos puntos con distancia conocida
                      </div>
                    )}
                  </div>

                  {/* Frame Selection Controls */}
                  <div className="bg-dark-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-dark-200">Selección de Frame</span>
                      <span className="text-xs text-dark-500">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </span>
                    </div>
                    
                    {/* Timeline slider */}
                    <div className="space-y-2">
                      <input
                        type="range"
                        min="0"
                        max={duration || 100}
                        step={1 / fps}
                        value={currentTime}
                        onChange={(e) => seekToTime(parseFloat(e.target.value))}
                        className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer
                                 [&::-webkit-slider-thumb]:appearance-none
                                 [&::-webkit-slider-thumb]:w-4
                                 [&::-webkit-slider-thumb]:h-4
                                 [&::-webkit-slider-thumb]:bg-primary-500
                                 [&::-webkit-slider-thumb]:rounded-full
                                 [&::-webkit-slider-thumb]:cursor-pointer"
                      />
                    </div>
                    
                    {/* Playback controls */}
                    <div className="flex items-center justify-center gap-2">
                      {/* Skip to start */}
                      <button
                        onClick={() => seekToTime(0)}
                        className="p-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Ir al inicio"
                      >
                        <ChevronsLeft className="w-4 h-4 text-dark-300" />
                      </button>
                      
                      {/* Back 10 frames */}
                      <button
                        onClick={() => stepFrame('backward', 10)}
                        className="p-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Retroceder 10 frames"
                      >
                        <SkipBack className="w-4 h-4 text-dark-300" />
                      </button>
                      
                      {/* Back 1 frame */}
                      <button
                        onClick={() => stepFrame('backward', 1)}
                        className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors text-xs text-dark-300"
                        title="Retroceder 1 frame"
                      >
                        -1
                      </button>
                      
                      {/* Play/Pause */}
                      <button
                        onClick={togglePlayPause}
                        className="p-3 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
                        title={isPlaying ? 'Pausar' : 'Reproducir'}
                      >
                        {isPlaying ? (
                          <Pause className="w-5 h-5 text-white" />
                        ) : (
                          <Play className="w-5 h-5 text-white" />
                        )}
                      </button>
                      
                      {/* Forward 1 frame */}
                      <button
                        onClick={() => stepFrame('forward', 1)}
                        className="px-3 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors text-xs text-dark-300"
                        title="Avanzar 1 frame"
                      >
                        +1
                      </button>
                      
                      {/* Forward 10 frames */}
                      <button
                        onClick={() => stepFrame('forward', 10)}
                        className="p-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Avanzar 10 frames"
                      >
                        <SkipForward className="w-4 h-4 text-dark-300" />
                      </button>
                      
                      {/* Skip to end */}
                      <button
                        onClick={() => seekToTime(duration)}
                        className="p-2 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Ir al final"
                      >
                        <ChevronsRight className="w-4 h-4 text-dark-300" />
                      </button>
                    </div>
                    
                    {/* Quick jump buttons */}
                    <div className="flex items-center justify-center gap-2 pt-2 border-t border-dark-700">
                      <span className="text-xs text-dark-500 mr-2">Ir a:</span>
                      {[0, 25, 50, 75, 100].map(percent => (
                        <button
                          key={percent}
                          onClick={() => seekToTime((duration * percent) / 100)}
                          className="px-2 py-1 text-xs bg-dark-700 hover:bg-dark-600 text-dark-400 rounded transition-colors"
                        >
                          {percent}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Distance input */}
                  {points.length === 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-3"
                    >
                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                        <p className="text-sm text-green-400 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Puntos seleccionados: {getPixelDistance(points[0], points[1]).toFixed(0)} píxeles
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">
                          Distancia real entre los puntos (metros)
                        </label>
                        <div className="flex gap-3">
                          <div className="relative flex-1">
                            <input
                              type="number"
                              value={realDistance}
                              onChange={(e) => setRealDistance(e.target.value)}
                              placeholder="Ej: 3.5"
                              step="0.1"
                              min="0.1"
                              className="w-full px-4 py-3 bg-dark-800/50 border border-dark-700 rounded-xl text-dark-100 placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500">
                              metros
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Calculated result */}
                      {calculatedPPM && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm text-dark-400">Escala calculada</p>
                              <p className="text-2xl font-bold font-mono text-primary-400">
                                {calculatedPPM.toFixed(2)} <span className="text-sm text-dark-400">px/m</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-dark-400">Resolución</p>
                              <p className="text-lg font-mono text-dark-200">
                                {(100 / calculatedPPM).toFixed(1)} cm/px
                              </p>
                            </div>
                          </div>
                          
                          {/* Preview at different speeds */}
                          <div className="bg-dark-900/50 rounded-lg p-3 mt-3">
                            <p className="text-xs text-dark-500 mb-2">Vista previa del efecto:</p>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              {[30, 60, 120].map(speed => (
                                <div key={speed} className="bg-dark-800 rounded p-2">
                                  <p className="text-xs text-dark-500">{speed} km/h</p>
                                  <p className="text-sm font-mono text-amber-400">
                                    {((speed / 3.6) * calculatedPPM / 30).toFixed(1)} px/frame
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Save location input */}
                      {calculatedPPM && (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={locationName}
                            onChange={(e) => setLocationName(e.target.value)}
                            placeholder="Nombre de ubicación (ej: Calle Mayor)"
                            className="flex-1 px-3 py-2 bg-dark-800/50 border border-dark-700 rounded-lg text-sm text-dark-100 placeholder-dark-500 focus:outline-none focus:border-green-500"
                          />
                          <button
                            onClick={saveCurrentCalibration}
                            disabled={!locationName.trim()}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                          >
                            <Save className="w-4 h-4" />
                            Guardar
                          </button>
                        </div>
                      )}

                      {/* Quick presets */}
                      <div>
                        <p className="text-xs text-dark-500 mb-2">Distancias de referencia comunes:</p>
                        <div className="flex flex-wrap gap-2">
                          {REFERENCE_OVERLAYS.map((ref) => (
                            <button
                              key={ref.id}
                              onClick={() => setRealDistance(ref.defaultMeters.toString())}
                              className={`px-3 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1 ${
                                realDistance === ref.defaultMeters.toString()
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-dark-800 text-dark-400 hover:bg-dark-700 hover:text-dark-200'
                              }`}
                            >
                              {ref.icon}
                              {ref.name} ({ref.defaultMeters}m)
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={resetCalibration}
                      className="flex-1 py-3 bg-dark-800 text-dark-200 font-medium rounded-xl hover:bg-dark-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reiniciar
                    </button>
                    <button
                      onClick={cancelCalibration}
                      className="flex-1 py-3 bg-dark-800 text-dark-200 font-medium rounded-xl hover:bg-dark-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancelar
                    </button>
                    <motion.button
                      whileHover={{ scale: calculatedPPM ? 1.02 : 1 }}
                      whileTap={{ scale: calculatedPPM ? 0.98 : 1 }}
                      onClick={applyCalibration}
                      disabled={!calculatedPPM}
                      className={`flex-1 py-3 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all ${
                        calculatedPPM
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-green-500/30'
                          : 'bg-dark-700 text-dark-500 cursor-not-allowed'
                      }`}
                    >
                      <Check className="w-4 h-4" />
                      Aplicar
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default CalibrationPanel;
